import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ImageShow from "../components/ImageShow";
import {Loader} from "../components/Loader/Loader";
import useBroker from "../hooks/use-broker";
import useDate from "../hooks/use-date";
import {
  setAccountName,
  useDeleteAccountMutation,
  useDeleteFileMutation,
} from "../store";
import { syncUnitPath } from "../constants/syncRoutes";
import Badge from "./Badge";
import Card from "./Card";
import Button from "./Button";
import FileRow from "./FileRow";
import AccountTileIcon from "./icons/AccountTileIcon";
import AlertCircle from "./icons/AlertCircle";
import ChevronRight from "./icons/ChevronRight";
import IndeterminateBar from "./syncunit/IndeterminateBar";
import { accountTitle, formatDate, formatPeriod, accountNumber, providerName, sizeInKb } from "./syncunit/format";
import { Tooltip } from "./Tooltip";

function UploadedAccount({ account }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [deleteAccount] = useDeleteAccountMutation();
  const [deleteFile] = useDeleteFileMutation();
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState("");
  const { broker } = useBroker(account.dataProvider);
  const { getDate } = useDate();

  const allReportsSynced = account.brokerReports.every(
    (report) => report.status === "SYNCED"
  );
  const hasAnyReport = account.brokerReports.length > 0;

  const accountName = useSelector((state) => {
    return state.file.accountName;
  });

  const changeTitle = (id, name) => {
    if (accountName.length > 0) {
      const found = accountName.find((item) => item.id === id);

      if (found) {
        const updatedItems = accountName.map((item) => {
          if (item.id === id) {
            return { id, name };
          }
          return item;
        });
        dispatch(setAccountName(updatedItems));
      } else {
        dispatch(setAccountName([...accountName, { id, name }]));
      }
    } else {
      dispatch(setAccountName([...accountName, { id, name }]));
    }
  };

  const handleChange = (event) => {
    const name = event.target.value;
    setTitle(name);
  };

  const editTitle = (event) => {
    event.preventDefault();
    setEdit(!edit);
  };

  const submitTitle = (id, event) => {
    event.preventDefault();
    const name = event.target[0].value;
    changeTitle(id, name);
    setEdit(false);
  };

  const removeAccount = (id) => {
    deleteAccount({
      id: id,
    });
  };

  const removeFile = (id) => {
    deleteFile({
      id: id,
    });
  };

  return (
    <div className="w-full pb-8">
      <div className="flex bg-gray-75 h-[90px] items-center rounded-t-2xl">
        <div className="py-5 pl-5">
          <ImageShow image={{ url: broker, label: broker }} />
        </div>
        <div className="py-5 flex flex-wrap">
          {!edit && (
            <>
              <p className="inline-block">{title ? title : account.name}</p>

              <Button onClick={editTitle} className="">
                <ImageShow
                  className="ml-2"
                  image={{ url: "edit", label: "Edit" }}
                />
              </Button>
              
              {hasAnyReport && (
                <div className="ml-3 inline-flex items-center">
                  <Tooltip
                    content={allReportsSynced ? t('upload.uploadedAccount.syncedTooltip') : t('upload.uploadedAccount.syncingTooltip')}
                    shadowed
                  >
                    {allReportsSynced ? (
                      <ImageShow
                        image={{ url: "checked-blue", label: "Synced" }}
                      />
                    ) : (
                      <Loader className="w-5 h-5" />
                    )}
                  </Tooltip>
                </div>
              )}
            </>
          )}
          {edit && (
            <form
              onSubmit={(event) => submitTitle(account.id, event)}
              className="flex">
              <input
                className="px-2 py-1 rounded-md text-gray-350 focus-visible:outline-cyan-450"
                type="text"
                onChange={handleChange}
                value={title ? title : account.name}
                accountId={account.id}
              />
              <Button className="ml-3 px-3 bg-gray-450 rounded-md">
                {t('upload.uploadedAccount.save')}
              </Button>
            </form>
          )}
          <p className="text-sm text-gray-350 basis-full">
            {t('upload.uploadedAccount.accountNumber')} {account.originalId}
          </p>
        </div>
        <Button
          disabled
          className="place-self-end self-center mr-6 ml-auto"
          onClick={() => removeAccount(account.id)}>
          <ImageShow image={{ url: "remove", label: "Remove" }} />
        </Button>
      </div>
      {account.brokerReports.map((file, index) => {
        const periodStart = getDate(file.periodStart);

        const periodEnd = getDate(file.periodEnd);
        return (
          <div
            key={file.id}
            className="flex items-center h-[75px] bg-gray-25 border-b border-gray-75">
            <div className="flex justify-center text-[28px] w-[75px] text-gray-350">
              {index + 1}
            </div>
            <div className="flex items-center gap-2">
              <div>
                <p className="text-sm">{file.fileName}</p>
                <span className="text-xs text-gray-350">
                  {file.fileSize / 1000} KB
                </span>
              </div>
              {file.status === "SYNCING" && (
                <Tooltip content={t('upload.uploadedAccount.fileSyncingTooltip')} shadowed>
                  <Loader className="w-4 h-4" />
                </Tooltip>
              )}
            </div>
            <div className="mr-8 text-sm ml-auto">
              <p className="text-sm">{t('upload.uploadedAccount.operations')}</p>
              <span className="text-xs text-gray-350">
                {file.transactionCount}
              </span>
            </div>
            <div className="mr-8">
              <p className="text-sm">{t('upload.uploadedAccount.reportPeriod')}</p>
              <span className="text-xs text-gray-350">
                {periodStart} – {periodEnd}
              </span>
            </div>
            <button
              className="place-self-end self-center mr-6"
              onClick={() => removeFile(file.id)}>
              <ImageShow image={{ url: "remove", label: "Remove" }} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// broker-report-sync AccountCard state from the row of readSynchronizingInvestingAccounts (screens.md §SCR-02).
export function accountCardState(account) {
  if (account.readError) return "unreadable";
  if (account.syncUnitStatus === "SYNCING") return "preparing";
  if (account.syncUnitStatus === "FAILED") return account.failedBrokerReportId ? "failed" : "failed-no-report";
  return "ready";
}

const STATUS_TEXT = {
  preparing: "syncUnits.row.preparing",
  ready: "syncUnits.row.ready",
  failed: "syncUnits.row.failed",
  "failed-no-report": "syncUnits.row.failedNoReport",
};

const STATUS_COLOR = {
  preparing: "text-text-muted",
  ready: "text-success-ink",
  failed: "text-danger",
  "failed-no-report": "text-danger",
};

// The 40 px tile of an account row (SCR-02, SCR-04): the card glyph, or the alert circle of an unreadable account.
export function AccountTile({ danger = false }) {
  return (
    <span
      data-testid="account-tile"
      aria-hidden="true"
      className={clsx("inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-xl", danger ? "bg-danger-bg" : "bg-surface-alt")}
    >
      {danger ? <AlertCircle /> : <AccountTileIcon />}
    </span>
  );
}

function LaunchReports({ account }) {
  const { t } = useTranslation();
  const reports = account.brokerReports || [];
  if (reports.length === 0) return null;

  return (
    <>
      <p className="text-xs font-semibold mt-4 mb-2 text-text-muted">{t("syncUnits.row.reports", { count: reports.length })}</p>
      <ul className="flex flex-col gap-2">
        {reports.map((report) => (
          <FileRow
            key={report.id}
            fileName={report.fileName}
            state={report.id === account.failedBrokerReportId ? "in-sync-failed" : "in-sync"}
            meta={t("syncUnits.report.meta", {
              provider: providerName(account.dataProvider),
              account: accountNumber(account.originalId),
              period: formatPeriod(report.periodStart, report.periodEnd),
              size: sizeInKb(report.fileSize),
              date: formatDate(report.createDateTime),
            })}
          />
        ))}
      </ul>
    </>
  );
}

// SCR-02 row: the state decides only whether the row opens SCR-03 (AC-05c); a row never carries actions.
export function AccountCard({ account }) {
  const { t } = useTranslation();
  const state = accountCardState(account);
  const preparing = state === "preparing";

  if (state === "unreadable") {
    return (
      <li data-testid="account-card" data-state={state} className="list-none">
        <Card variant="danger" data-testid="account-surface" className="px-5 py-4 flex items-center gap-4">
          <AccountTile danger />
          <div>
            <p className="text-sm font-medium">{t("syncUnits.row.unreadableTitle")}</p>
            <p className="text-xs text-danger mt-0.5">{t("syncUnits.row.unreadableText")}</p>
          </div>
        </Card>
      </li>
    );
  }

  const title = (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium break-all min-w-0">{accountTitle(account)}</span>
        <Badge tone="accent">{providerName(account.dataProvider)}</Badge>
        <Badge tone="muted">{t(account.status === "DRAFT" ? "syncCommon.badges.newAccount" : "syncCommon.badges.existingAccount")}</Badge>
      </div>
      {!preparing && <p className={clsx("text-xs mt-0.5", STATUS_COLOR[state])}>{t(STATUS_TEXT[state])}</p>}
    </div>
  );

  const surface = "px-5 py-4";
  return (
    <li data-testid="account-card" data-state={state} aria-busy={preparing ? "true" : undefined} className="list-none">
      {preparing ? (
        <Card data-testid="account-surface" className={surface}>
          <div data-testid="account-header" className="flex items-center gap-4">
            {title}
          </div>
          <IndeterminateBar label={t("syncUnits.row.preparing")} className="mt-3" />
          <p className={clsx("text-xs mt-2", STATUS_COLOR.preparing)}>{t(STATUS_TEXT.preparing)}</p>
          <LaunchReports account={account} />
        </Card>
      ) : (
        <Card
          variant="interactive"
          to={syncUnitPath(account.id)}
          data-testid="account-surface"
          className={clsx(surface, state !== "ready" && "border-danger-border")}
        >
          <div data-testid="account-header" className="flex items-center gap-4">
            <AccountTile />
            {title}
            <ChevronRight />
          </div>
          <LaunchReports account={account} />
        </Card>
      )}
    </li>
  );
}

export default UploadedAccount;
